/**
 * The flight information display, and what it is not saying.
 *
 * Two halves on one screen, and the second is the point. The top is the board
 * as a passenger sees it — big, plain, and in the public vocabulary. Beneath
 * it is the same board read back as a list of the places it could not help:
 * the movements with no time, no gate, or nothing but "please ask staff".
 *
 * An airport judges a display by whether it is up. A passenger judges it by
 * whether it answered them. The gaps list is the difference, and it is for
 * the operator to clear before the hall fills up.
 *
 * Nothing on the public half can be typed. Every status is derived from the
 * movement's own record, so the board cannot disagree with the operation.
 *
 * The public half comes in the three displays an airport hangs: the hall
 * board, the screen above a gate, and the screen above a reclaim belt. Each
 * reads the same rows, in Arabic and English by turns, with the cities named,
 * the desks and belts read from the allocation plan, and a line of what the
 * hall is being told. "Show on a wall screen" fills the screen with it and
 * pages through a long board the way a hall display does.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSession } from "../../app/SessionContext";
import { type Flight } from "../../domain/flights";
import { fidsBoard, fidsGaps, FIDS_LABEL, type FidsStatus } from "../../domain/fids";
import {
  announcements,
  beltScreen,
  boardBelts,
  boardGates,
  carrierHue,
  cityName,
  deskSpan,
  gateScreen,
  plannedPlaces,
  publicGaps,
  publicRows,
  say,
  type PublicRow,
} from "../../domain/fidsPublic";
import { opsState } from "../../domain/flightDelays";
import { Button, Card, EmptyState, Field, Segmented, Select, Table } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { DayNotes, DayState, FlightTheme, hasDay, LiveStrip } from "./FlightTheme";
import { useScreenContext } from "../../assistant/screen";
import { useFlightWorld } from "./useFlightWorld";
import { getLang, t, tn, type Lang } from "../../i18n";
import s from "./Flights.module.css";
import { hhmm as clockTime } from "../../lib/format";

/* A departures board leaves an unknown time blank. A dash on the wall of a
   terminal reads as a time that has been withdrawn. */
const hhmm = (iso: string | null | undefined) => clockTime(iso, "");

type Display = "hall" | "gate" | "belt";
type BoardLang = "both" | "en" | "ar";

/** Seconds each language stays up when the board alternates. PROPOSED. */
const LANG_EVERY = 10;
/** Rows on one page of a wall screen, and how long a page stays up. PROPOSED. */
const WALL_ROWS = 12;
const PAGE_EVERY = 12;

/** A ticking clock, at whatever pace the caller needs. */
function useNow(everyMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

/**
 * The language the board is showing now. When it alternates it starts in the
 * console's own language, so the person setting it up reads it first.
 */
function useBoardLang(mode: BoardLang): Lang {
  const [phase, setPhase] = useState<Lang>(() => getLang());
  useEffect(() => {
    if (mode !== "both") return;
    const id = setInterval(() => setPhase((p) => (p === "en" ? "ar" : "en")), LANG_EVERY * 1000);
    return () => clearInterval(id);
  }, [mode]);
  return mode === "both" ? phase : mode;
}

export function FidsPage() {
  const session = useSession();
  /* the flight record and the allocation plan — the gates, desks and belts are the plan's */
  const world = useFlightWorld();
  const { flights, register, day } = world;
  const [dir, setDir] = useState<Flight["direction"]>("departure");
  const [display, setDisplay] = useState<Display>("hall");
  const [langMode, setLangMode] = useState<BoardLang>("both");
  const [wall, setWall] = useState(false);
  const lang = useBoardLang(langMode);
  /* statuses move with the clock — boarding opens, gates close — so the rows are re-read */
  const now = useNow(30_000);

  const rows = useMemo(
    () => fidsBoard(flights, now, dir, register),
    [flights, register, dir, now],
  );
  const gaps = useMemo(() => fidsGaps(rows), [rows]);

  /* the public words: cities, airlines, and the desks and belts the allocation plan placed */
  const places = useMemo(() => plannedPlaces(day?.demands, day?.plan), [day]);
  const both = useMemo(() => {
    const deps = fidsBoard(flights, now, "departure", register);
    const arrs = fidsBoard(flights, now, "arrival", register);
    return publicRows([...deps, ...arrs], flights, places, now);
  }, [flights, register, now, places]);
  const pub = useMemo(() => both.filter((r) => r.direction === dir), [both, dir]);
  const extra = useMemo(() => publicGaps(pub, now), [pub, now]);

  const board = [
    t("{site}: {n} on the public board.", {
      site: session.site.code,
      n: tn(rows.length, "{n} movement", "{n} movements"),
    }),
  ];
  if (gaps.noTime.length || gaps.askStaff.length) {
    board.push(
      t("{noTime} carry no time a passenger can use, and {ask} say only that somebody should ask staff.", {
        noTime: gaps.noTime.length,
        ask: gaps.askStaff.length,
      }),
    );
  }
  if (extra.noCity.length || extra.noDesks.length || extra.noBelt.length) {
    board.push(
      t("{city} show a code instead of a city, {desks} have no check-in desks in the plan and {belt} no belt.", {
        city: extra.noCity.length,
        desks: extra.noDesks.length,
        belt: extra.noBelt.length,
      }),
    );
  }

  useScreenContext({
    route: "/fids",
    title: "Flight information",
    worksInFull: true,
    summary: board,
    figures: new Set(
      [
        rows.length,
        gaps.noTime.length,
        gaps.noGate.length,
        gaps.askStaff.length,
        extra.noCity.length,
        extra.noDesks.length,
        extra.noBelt.length,
      ]
        .map(String)
        .concat(Array.from({ length: 13 }, (_, i) => String(i))),
    ),
    own: (q) =>
      /gap|missing|no time|no gate|ask|city|desk|belt/.test(q.toLowerCase())
        ? [
            tn(
              gaps.noTime.length,
              "{n} movement has no time on the board.",
              "{n} movements have no time on the board.",
            ),
            tn(gaps.noGate.length, "{n} departure has no gate.", "{n} departures have no gate."),
            tn(
              gaps.askStaff.length,
              "{n} tells the passenger only to ask staff.",
              "{n} tell the passenger only to ask staff.",
            ),
            tn(extra.noCity.length, "{n} shows a code instead of a city.", "{n} show a code instead of a city."),
            tn(
              extra.noDesks.length,
              "{n} departure due soon has no check-in desks in the plan.",
              "{n} departures due soon have no check-in desks in the plan.",
            ),
            tn(extra.noBelt.length, "{n} arrival has no belt in the plan.", "{n} arrivals have no belt in the plan."),
          ].join("\n")
        : null,
    offers: [{ label: "What is the board not saying?", ask: "what gaps are on the board?" }],
  });

  const gapRows = [...new Set([...gaps.noTime, ...gaps.noGate, ...gaps.askStaff])];
  const gapIds = new Set(gapRows.map((r) => r.flightId));
  const extraOnly = [...extra.noCity, ...extra.noDesks, ...extra.noBelt].filter((r) => !gapIds.has(r.flightId));
  const extraRows = [...new Map(extraOnly.map((r) => [r.flightId, r])).values()];
  const reasonsFor = (id: string) => {
    const pr = pub.find((r) => r.flightId === id);
    const r = rows.find((x) => x.flightId === id);
    return [
      r && gaps.noTime.includes(r) ? t("no time a passenger can use") : null,
      r && gaps.noGate.includes(r) ? t("no gate and no bus point") : null,
      r && gaps.askStaff.includes(r) ? t("nothing but 'ask staff'") : null,
      pr && extra.noCity.includes(pr) ? t("a code, not a city: {code} is not in the city list", { code: pr.place }) : null,
      pr && extra.noDesks.includes(pr) ? t("no check-in desks in the allocation plan") : null,
      pr && extra.noBelt.includes(pr) ? t("no reclaim belt in the allocation plan") : null,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const lede = (
    <p className={s.lede}>
      {t(
        "The public flight board, and what it can't tell passengers yet.",
      )}
    </p>
  );
  if (!hasDay(world))
    return (
      <FlightTheme>
        <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Flight information">
          {lede}
          <DayState world={world} />
        </Page>
      </FlightTheme>
    );

  return (
    <FlightTheme>
      <Page
        eyebrow={t("{site} · Flight operations", { site: session.site.code })}
        title="Flight information"
        actions={
          <Segmented
            ariaLabel={t("Departures or arrivals")}
            value={dir}
            onChange={setDir}
            options={[
              { value: "departure" as const, label: t("Departures") },
              { value: "arrival" as const, label: t("Arrivals") },
            ]}
          />
        }
      >
        <LiveStrip flights={flights} site={session.site.code} />
        {lede}
        <DayNotes world={world} />

        <div className={s.fidsControls}>
          <Segmented
            ariaLabel="Which display"
            value={display}
            onChange={setDisplay}
            options={[
              { value: "hall" as const, label: "Hall board" },
              { value: "gate" as const, label: "Gate screen" },
              { value: "belt" as const, label: "Belt screen" },
            ]}
          />
          <Segmented
            ariaLabel="Board language"
            value={langMode}
            onChange={setLangMode}
            options={[
              { value: "both" as const, label: "Arabic and English by turns" },
              { value: "en" as const, label: "English" },
              { value: "ar" as const, label: "العربية" },
            ]}
          />
          <Button size="sm" onClick={() => setWall(true)} data-testid="fids-wall-open">
            ⛶ {t("Show on a wall screen")}
          </Button>
        </div>

        <WallFrame wall={wall} onExit={() => setWall(false)}>
          {display === "hall" && (
            <HallBoard
              rows={pub}
              dir={dir}
              lang={lang}
              wall={wall}
              site={session.site.code}
              flights={flights}
              tickerRows={both}
            />
          )}
          {display === "gate" && <GateDisplay rows={both} lang={lang} wall={wall} />}
          {display === "belt" && <BeltDisplay rows={both} lang={lang} wall={wall} />}
        </WallFrame>

        {/* and the half the public never sees */}
        <Card
          collapsible
          title="What the board can't tell passengers"
          sub="Flights missing a time, gate, or city"
        >
          {gapRows.length === 0 && extraRows.length === 0 ? (
            <EmptyState>{t("Every movement on the board carries a time, a gate and a status.")}</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Flight")}</th>
                  <th>{t("What is missing")}</th>
                </tr>
              </thead>
              <tbody data-testid="fids-gaps">
                {[...gapRows, ...extraRows].map((r) => (
                  <tr key={r.flightId}>
                    <td>
                      <b>{r.flight}</b>
                      <div className={s.why}>{r.place}</div>
                    </td>
                    <td className={s.bad}>{reasonsFor(r.flightId)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <p className={s.meta}>
            {t(
              "A canceled flight shows as canceled. The reason is never shown to the public.",
            )}
          </p>
          <p className={s.meta}>
            {t(
              "Check-in desks and baggage belts come from the allocation plan.",
            )}
          </p>
        </Card>
      </Page>
    </FlightTheme>
  );
}

/* ── the wall screen ──────────────────────────────────────────────────── */

/**
 * In the page, the display sits in the flow. On a wall it fills the screen —
 * the browser's full screen where the page is allowed it, the whole window
 * where it is not — and Esc brings it back.
 */
function WallFrame({ wall, onExit, children }: { wall: boolean; onExit: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!wall) return;
    const el = ref.current;
    el?.requestFullscreen?.().catch(() => undefined);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onExit();
    const onFs = () => !document.fullscreenElement && onExit();
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => undefined);
    };
  }, [wall, onExit]);
  return (
    <div ref={ref} className={wall ? s.wall : undefined} data-testid={wall ? "fids-wall" : undefined}>
      {wall && (
        <button type="button" className={s.wallExit} onClick={onExit} aria-label={t("Leave the wall screen")}>
          ✕
        </button>
      )}
      {children}
    </div>
  );
}

/** A value that turns over like a split-flap when it changes. */
function Flap({ v, className }: { v: string; className?: string }) {
  return (
    <span key={v} className={`${s.flap} ${className ?? ""}`}>
      {v}
    </span>
  );
}

function CarrierTile({ r, lang }: { r: PublicRow; lang: Lang }) {
  return (
    <span className={s.carrier}>
      <span className={s.carrierTile} style={{ ["--h" as string]: carrierHue(r.carrier) }} aria-hidden>
        {r.carrier ?? "··"}
      </span>
      <span className={s.carrierText}>
        <Flap v={r.flight} className={s.fidsFlight} />
        {r.airline && <small>{r.airline[lang]}</small>}
      </span>
    </span>
  );
}

function StatusCell({ status, lang, late }: { status: FidsStatus; lang: Lang; late?: boolean }) {
  const live = status === "boarding" || status === "final_call";
  return (
    <span className={s.statusWrap}>
      {(live || late) && <span className={s.blink} data-status={late ? "door_late" : status} aria-hidden />}
      <Flap v={say(lang, FIDS_LABEL[status])} />
    </span>
  );
}

function BoardHead({ title, lang, sub }: { title: { en: string; ar: string }; lang: Lang; sub?: string }) {
  const now = useNow(1000);
  return (
    <header className={s.fidsHead}>
      <span className={s.fidsTitle}>
        <span className={s.fidsPlane} aria-hidden>
          ✈
        </span>
        <span>
          <b>{lang === "ar" ? title.ar : title.en}</b>
          <small dir={lang === "ar" ? "ltr" : "rtl"}>{lang === "ar" ? title.en : title.ar}</small>
        </span>
        {sub && <span className={s.fidsSub}>{sub}</span>}
      </span>
      <span className={s.fidsClock}>
        <small>
          {now.toLocaleDateString(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
          })}
        </small>
        {now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </span>
    </header>
  );
}

function Ticker({ lines }: { lines: string[] }) {
  if (!lines.length) return null;
  const text = lines.join("   ◆   ");
  return (
    <div className={s.ticker} data-testid="fids-ticker" aria-live="off">
      <span className={s.tickerTag}>i</span>
      <div className={s.tickerTrack}>
        <span style={{ animationDuration: `${Math.max(25, text.length / 6)}s` }}>{text}</span>
      </div>
    </div>
  );
}

/* ── the hall board ───────────────────────────────────────────────────── */

function HallBoard({
  rows,
  dir,
  lang,
  wall,
  site,
  flights,
  tickerRows,
}: {
  rows: PublicRow[];
  dir: Flight["direction"];
  lang: Lang;
  wall: boolean;
  site: string;
  flights: readonly Flight[];
  tickerRows: PublicRow[];
}) {
  const now = new Date();
  /* a wall screen pages through a long board; in the page every row is in view */
  const pages = wall ? Math.max(1, Math.ceil(rows.length / WALL_ROWS)) : 1;
  const [turn, setTurn] = useState(0);
  useEffect(() => {
    if (pages < 2) return;
    const id = setInterval(() => setTurn((p) => p + 1), PAGE_EVERY * 1000);
    return () => clearInterval(id);
  }, [pages]);
  const page = turn % pages;
  const shown = wall ? rows.slice(page * WALL_ROWS, (page + 1) * WALL_ROWS) : rows;
  const dep = dir === "departure";
  const ticker = announcements(tickerRows, lang, site);

  return (
    <section className={s.fids} data-testid="fids-board" data-lang={lang} dir={lang === "ar" ? "rtl" : "ltr"}>
      <BoardHead title={dep ? { en: "Departures", ar: "المغادرة" } : { en: "Arrivals", ar: "القدوم" }} lang={lang} />
      {rows.length === 0 ? (
        <EmptyState>{t("Nothing is due in the next few hours.")}</EmptyState>
      ) : (
        <table className={s.fidsTable}>
          <thead>
            <tr>
              <th>{say(lang, "Time")}</th>
              <th>{say(lang, "Flight")}</th>
              <th>{say(lang, dep ? "To" : "From")}</th>
              <th>{say(lang, "Expected")}</th>
              <th>{say(lang, dep ? "Check-in" : "Belt")}</th>
              <th>{say(lang, "Gate")}</th>
              <th>{say(lang, "Status")}</th>
            </tr>
          </thead>
          <tbody key={`${page}`} className={s.fidsPage}>
            {shown.map((r) => {
              const f = flights.find((x) => x.id === r.flightId);
              const ops = f ? opsState(f, now) : undefined;
              return (
                <tr key={r.flightId} data-testid={`fids-${r.flight}`} data-status={r.status} data-ops={ops}>
                  <td className={s.fidsTime}>
                    <Flap v={hhmm(r.scheduled)} />
                  </td>
                  <td>
                    <CarrierTile r={r} lang={lang} />
                  </td>
                  <td className={s.fidsCity}>
                    <Flap v={cityName(r, lang)} />
                  </td>
                  <td className={s.fidsTime}>
                    {r.shown ? (
                      <Flap v={hhmm(r.shown)} className={r.status === "delayed" ? s.fidsLate : undefined} />
                    ) : r.status === "delayed" ? (
                      <span className={s.fidsQuiet}>{say(lang, "to follow")}</span>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className={s.fidsFlight}>
                    <Flap v={dep ? deskSpan(r.counters) : (r.belt ?? "")} />
                  </td>
                  <td className={s.fidsFlight}>
                    {r.gate ? (
                      <span className={s.gateBox}>
                        <Flap v={r.gate} />
                      </span>
                    ) : r.note ? (
                      <span className={s.fidsQuiet}>{say(lang, "by bus from the gate")}</span>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className={s.fidsStatus} data-status={r.status}>
                    <StatusCell status={r.status} lang={lang} late={ops === "door_late"} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {pages > 1 && (
        <div className={s.fidsPager} aria-label={say(lang, "Page {n} of {of}", { n: page + 1, of: pages })}>
          {Array.from({ length: pages }, (_, i) => (
            <span key={i} data-on={i === page} />
          ))}
        </div>
      )}
      <Ticker lines={ticker} />
    </section>
  );
}

/* ── the screen above a gate ──────────────────────────────────────────── */

function GateDisplay({ rows, lang, wall }: { rows: PublicRow[]; lang: Lang; wall: boolean }) {
  const gates = boardGates(rows);
  const [gate, setGate] = useState<string>("");
  /* open on a gate with something happening at it: boarding first */
  const busiest =
    rows.find((r) => r.gate && (r.status === "final_call" || r.status === "boarding"))?.gate ?? gates[0] ?? "";
  const chosen = gates.includes(gate) ? gate : busiest;
  const now = useNow(15_000);
  const screen = gateScreen(rows, chosen, now);
  const c = screen.current;

  return (
    <>
      {!wall && (
        <div className={s.fidsPick}>
          <Field label="Gate">
            <Select value={chosen} onChange={(e) => setGate(e.target.value)} data-testid="fids-gate-pick">
              {gates.map((g) => (
                <option key={g} value={g}>
                  {t("Gate {gate}", { gate: g })}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}
      <section className={s.fids} data-testid="fids-gate" dir={lang === "ar" ? "rtl" : "ltr"}>
        <BoardHead title={{ en: `Gate ${chosen}`, ar: `البوابة ${chosen}` }} lang={lang} />
        {!c ? (
          <EmptyState>{say(lang, "No departure is due at this gate.")}</EmptyState>
        ) : (
          <div className={s.gateHero} data-status={c.status}>
            <div className={s.gateMain}>
              <CarrierTile r={c} lang={lang} />
              <div className={s.gateCity}>
                <Flap v={cityName(c, "en")} />
                {c.city && <span dir="rtl">{c.city.ar}</span>}
              </div>
              <div className={s.gateTimes}>
                <span>
                  <small>{say(lang, "Time")}</small>
                  <b>{hhmm(c.scheduled)}</b>
                </span>
                {c.shown && c.shown !== c.scheduled && (
                  <span>
                    <small>{say(lang, "Expected")}</small>
                    <b className={s.fidsLate}>{hhmm(c.shown)}</b>
                  </span>
                )}
              </div>
            </div>
            <div className={s.gateBand} data-status={c.status}>
              <StatusCell status={c.status} lang={lang} />
              {screen.closesIn !== null && (
                <span className={s.gateCountdown} data-testid="fids-gate-closes">
                  {say(lang, "Gate closes in {n} min", { n: screen.closesIn })}
                </span>
              )}
            </div>
          </div>
        )}
        {screen.next.length > 0 && (
          <div className={s.gateNext}>
            <small>{say(lang, "Next at this gate")}</small>
            {screen.next.map((r) => (
              <span key={r.flightId}>
                <b>{hhmm(r.scheduled)}</b> {r.flight} · {cityName(r, lang)}
              </span>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/* ── the screen above a reclaim belt ──────────────────────────────────── */

function BeltDisplay({ rows, lang, wall }: { rows: PublicRow[]; lang: Lang; wall: boolean }) {
  const belts = boardBelts(rows);
  const [belt, setBelt] = useState<string>("");
  const landed = rows.find((r) => r.direction === "arrival" && r.status === "landed" && r.belt)?.belt;
  const chosen = belts.includes(belt) ? belt : (landed ?? belts[0] ?? "");
  const here = beltScreen(rows, chosen);
  const num = chosen.replace(/\D/g, "") || chosen;

  return (
    <>
      {!wall && (
        <div className={s.fidsPick}>
          <Field label="Belt">
            <Select value={chosen} onChange={(e) => setBelt(e.target.value)} data-testid="fids-belt-pick">
              {belts.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}
      <section className={s.fids} data-testid="fids-belt" dir={lang === "ar" ? "rtl" : "ltr"}>
        <BoardHead title={{ en: "Baggage reclaim", ar: "استلام الأمتعة" }} lang={lang} />
        <div className={s.beltHero}>
          <span className={s.beltNum} aria-label={chosen}>
            {num}
          </span>
          <div className={s.beltList}>
            {here.length === 0 ? (
              <EmptyState>{say(lang, "No flight is due on this belt.")}</EmptyState>
            ) : (
              here.map((r) => (
                <div key={r.flightId} className={s.beltRow} data-status={r.status}>
                  <CarrierTile r={r} lang={lang} />
                  <span className={s.fidsCity}>
                    <Flap v={cityName(r, lang)} />
                  </span>
                  <span className={s.fidsTime}>
                    {r.status === "landed"
                      ? say(lang, "Landed {time}", { time: hhmm(r.shown) })
                      : r.shown
                        ? say(lang, "Expected {time}", { time: hhmm(r.shown) })
                        : hhmm(r.scheduled)}
                  </span>
                  <span className={s.fidsStatus} data-status={r.status}>
                    <StatusCell status={r.status} lang={lang} />
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
}
